import {
  Dimension,
  HierarchyDetails,
  IHierarchicalItemsStore
} from '@components/UI-Components/HierarchyWiseItems'
import {
  CATEGORY_INTERNAL_SEPERATOR,
  INDIVIDUAL_SEPARATOR_IDENTIFIER,
  PAGE_SEPARATOR_IDENTIFIER
} from '@components/UI-Components/HierarchyWiseItems/constants'
import { ACTIVITY_HIERARCHY_MAP, DEFAULT_ACTIVITY_HIERARCHY } from '@constants'
import {
  CATEGORY_UI_SEPERATOR,
  HIERARCHY_ELEMENT_HEIGHT,
  HIERARCHY_ROOT_ID
} from '@modules/order/constants/uiConstants'
import { PRODUCT_CARD_DIMENSIONS } from '@modules/showroom/productCollection/constants/uiConstants'
import { nav } from '@stores/nav'
import { extractIndexFromId } from '@utils/index-from-id'
import concat from 'lodash/concat'
import difference from 'lodash/difference'
import fill from 'lodash/fill'
import find from 'lodash/find'
import findKey from 'lodash/findKey'
import findLastKey from 'lodash/findLastKey'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import mapValues from 'lodash/mapValues'
import maxBy from 'lodash/maxBy'
import memoize from 'lodash/memoize'
import omit from 'lodash/omit'
import pickBy from 'lodash/pickBy'
import sortBy from 'lodash/sortBy'
import values from 'lodash/values'
import { action, computed, observable, reaction, transaction, when } from 'mobx'
import { computedFn } from 'mobx-utils'
import { ProductCardSizes } from '@constants'
import ActiveItemStore from './ActiveItemStore'
import { ElementRefStore } from './ElementRefStore'
import { HierarchyScrollPositionStore } from './HierarchyScrollPositionStore'
import { DefaultDimension, ItemDimensionStore } from './ItemDimensionStore'
import { PaginatedHierarchyItemsDataStore } from './PaginatedHierarchyItemsDataStore'
import { stores } from '@stores'
import { getProductAttributeValue } from '@utils/get-product-attribute-value'
import { reduce } from 'lodash'
import { ENHANCED_VIEW_ROUTES } from '@modules/showroom/productCollection/enhancedView/constants/cnf-product-constants'
import { View } from '@modules/order/constants/views'

export interface Dimensions {
  width: number
  height: number
}

export enum ItemType {
  INDIVIDUAL,
  PAGE,
  ROOT,
  CATEGORY
}

/**
 * This class is responsible for UI related data management it will modify store data as necessary to UI
 *
 * @TODO Remove product teminology
 */

export abstract class PaginatedHierarchyItemsUIStore<
  T extends PaginatedHierarchyItemsDataStore
> implements IHierarchicalItemsStore
{
  elementRefStore = new ElementRefStore()
  scrollPositionStore = new HierarchyScrollPositionStore(this.elementRefStore)
  activeItemStore = new ActiveItemStore()
  hierarchyRootId = ''
  dimensionMapper

  constructor(
    public dataStore: T,
    viewDimensions: DefaultDimension = {
      [ItemType.CATEGORY]: { height: HIERARCHY_ELEMENT_HEIGHT } as Dimension,
      [ItemType.INDIVIDUAL]: PRODUCT_CARD_DIMENSIONS[ProductCardSizes.Large] as Dimension,
      [ItemType.PAGE]: PRODUCT_CARD_DIMENSIONS[ProductCardSizes.Large] as Dimension,
      [ItemType.ROOT]: {
        height: 100
      }
    },
    hierarchyRootId = HIERARCHY_ROOT_ID
  ) {
    this.hierarchyRootId = hierarchyRootId
    this.dimensionMapper = new ItemDimensionStore(this.hierarchyRootId)
    this.dimensionMapper.setDefault(viewDimensions)
    this.updateViewDimensions(viewDimensions)
  }

  protected caches: any = {}
  private reactions = []

  @observable isSidebarOpen = false
  @observable isFilterActive = false
  @observable isGroupByActive = false
  @observable isSearchActive = false
  @observable isSearchTouched = false
  @observable isFilterTouched = false
  @observable isGroupByTouched = false
  @observable isEnhancedSortByTouched = false
  @observable isCardSizeModalOpen = false
  @observable isSortActive = false
  @observable isEnhancedSortActive = false
  @observable isSortTouched = false
  @observable isProductRemoving = false

  @computed get currentHierarchy() {
    if (this.scrollPositionStore.scrolledNode?.id) {
      const scrolledNodeId = this.scrollPositionStore.scrolledNode.id
      if (scrolledNodeId.split(CATEGORY_INTERNAL_SEPERATOR).length === 1) {
        let nodeId = ''
        if (scrolledNodeId.includes(CATEGORY_INTERNAL_SEPERATOR)) {
          nodeId = scrolledNodeId.split(CATEGORY_INTERNAL_SEPERATOR)[0]
        } else {
          nodeId = scrolledNodeId
            .split(INDIVIDUAL_SEPARATOR_IDENTIFIER)?.[0]
            ?.split(PAGE_SEPARATOR_IDENTIFIER)?.[0]
            ?.split(CATEGORY_INTERNAL_SEPERATOR)?.[0]
        }
        return this.getFirstFromHierarchy([
          find(this.dataStore.heirarchy, ['name', nodeId])
        ])
      } else {
        return scrolledNodeId
          .split(INDIVIDUAL_SEPARATOR_IDENTIFIER)?.[0]
          ?.split(PAGE_SEPARATOR_IDENTIFIER)?.[0]
          ?.split(CATEGORY_INTERNAL_SEPERATOR)
      }
    } else {
      return this.getFirstFromHierarchy(this.dataStore?.heirarchy) || []
    }
  }

  /**
   * This will return heirarchy object as required by Hierachy component
   *
   * @example
   * {
   *  activities: {
   *    children: ['Accessories', ...],
   *    itemHeight: 30
   *  },
   *  'Accessories': {
   *    name: 'Accessories',
   *    itemHeight: 30,
   *    ...
   *  }
   * }
   */
  @computed get hierarchyDetails(): HierarchyDetails {
    // @TO DO remove this once moved to mobx as inheritance is supported in mobx6
    if (!(this instanceof PaginatedHierarchyItemsUIStore)) {
      return {}
    }
    if (!this.dataStore.heirarchy?.length) {
      return {
        [`${this.hierarchyRootId}`]: {
          children: [],
          startIndex: null,
          endIndex: null,
          depth: -1
        }
      }
    }
    const generatedHierrachy = this.generateTableDataRecursively(
      this.dataStore.heirarchy,
      '',
      0,
      0,
      0
    )
    const {
      productConfiguration: { enhancedHeaderEnabled }
    } = stores

    if (enhancedHeaderEnabled) {
      const childItems = this.prepareHierarchyForScroll()
      const heirarchy: any = pickBy(
        generatedHierrachy,
        hierarchyObj => typeof hierarchyObj === 'object'
      )
      return {
        [this.hierarchyRootId]: {
          children: childItems
        },
        ...heirarchy
      }
    }
    // omit finished key from heirarchy details
    const heirarchy: any = pickBy(generatedHierrachy, hierarchyObj => hierarchyObj.isLeaf)
    return {
      [this.hierarchyRootId]: {
        children: this.getSortedHeirarchy(heirarchy)
      },
      ...heirarchy
    }
  }

  prepareHierarchyForScroll = () => {
    const mainArr = []
    // traverse all nodes and create array of all paths [2D array]
    this.dataStore.heirarchy.forEach(item => {
      this.getLeafOfAllChild(item, [], mainArr)
    })
    // remove leaf node from the all paths
    mainArr.forEach(el => {
      if (el.length > 1) {
        el.pop()
      }
    })
    // join the paths by CATEGORY_INTERNAL_SEPERATOR
    const joined = mainArr.map(el => el.join(CATEGORY_INTERNAL_SEPERATOR))
    // create flattened array and remove duplicate paths
    return joined.flat().filter((el, index, ar) => {
      return ar.indexOf(el) === index
    })
  }

  getLeafOfAllChild = (data, acc = [], mainArr = []) => {
    if (data) {
      if (data.children?.length) {
        acc.push(data.name)
        data.children.forEach(el => {
          const arr = [...acc]
          this.getLeafOfAllChild(el, arr, mainArr)
        })
      } else {
        acc.push(data.name)
        mainArr.push([...acc])
      }
    }
  }

  @computed get isEnhancedViewEnabled() {
    const {
      productConfiguration: { enhancedViewEnabled }
    } = stores
    return enhancedViewEnabled
  }

  @computed get isEnhancedHeaderEnabled() {
    const {
      productConfiguration: { enhancedHeaderEnabled }
    } = stores
    return enhancedHeaderEnabled
  }

  @computed get filterAttributes() {
    return this.dataStore.filterStore.filterAttributes?.reduce?.(
      (acc, { displayName, values }) => ({
        ...acc,
        [displayName]: values.map(({ label }) => label)
      }),
      this.dataStore.filterStore.hasFilterAttributes
        ? { ...this.dataStore.staticFilterValues }
        : {}
    )
  }

  @computed get navHierarchyDetails() {
    return this.scrollPositionStore.hierarchy
  }

  getSortedHeirarchy = computedFn(heirarchy =>
    this.caches?.sortedHierarchyDataCache?.(heirarchy)
  )

  /**
   * This method will recursively iterate each heirarchy and will return start and end index for leaf elements
   */
  generateTableDataRecursively = computedFn(
    (children, groupName, finished = 0, depth = 0, groupTotalItems = 0) =>
      this.caches?.tableDataCache?.(children, groupName, finished, depth, groupTotalItems)
  )

  @action
  init() {
    this.dataStore.init()
    this.initializeCaches()
    this.initializeReactions()
  }

  getCurrentHierarchyHeaderText = computedFn((id: string) => {
    let hierarchyHeaderText = ''
    if (id && !isEmpty(this.scrollPositionStore?.hierarchy)) {
      const currentHierarchy = id.split(CATEGORY_INTERNAL_SEPERATOR)
      const arr = []
      hierarchyHeaderText = currentHierarchy.reduce((acc, elem, index) => {
        arr.push(elem)
        let selector = index === 0 ? elem : ''
        if (index > 0) {
          selector = arr.join(CATEGORY_INTERNAL_SEPERATOR)
        }

        // add total products to header
        const { totalItems } = this.scrollPositionStore.hierarchy?.[selector]
        acc = `${acc}${elem} ${totalItems} ACs${
          currentHierarchy.length - 1 === index ? '' : CATEGORY_UI_SEPERATOR
        }`
        return acc
      }, '')
    }

    return hierarchyHeaderText.toLowerCase()
  })

  getActualNodeName = selector => {
    const {
      productConfiguration: { defaultValuesFromConfig, findSpecificAttribute }
    } = stores
    let nodeText = ''
    try {
      const groupObj = this.scrollPositionStore.hierarchy?.[selector]
      const attributeObj = findSpecificAttribute(groupObj?.attributeId)
      if (attributeObj && groupObj) {
        nodeText = getProductAttributeValue(
          groupObj,
          attributeObj,
          true,
          defaultValuesFromConfig
        )
      }
    } catch (e) {
      console.error(e)
    }
    return nodeText
  }

  getEnhancedHeaderObj = computedFn((id: string) => {
    let obj: any = {}
    if (id && !isEmpty(this.scrollPositionStore?.hierarchy)) {
      const itemData = this.scrollPositionStore.hierarchy?.[id] || {}
      const { totalItems: totalProducts = 0, children: childItems } = itemData
      if (childItems?.length > 0) {
        const arr = []
        const allNames = id
          .split(CATEGORY_INTERNAL_SEPERATOR)
          .map((elem, index) => {
            arr.push(elem)
            let selector = index === 0 ? elem : ''
            if (index > 0) {
              selector = arr.join(CATEGORY_INTERNAL_SEPERATOR)
            }
            return this.getActualNodeName(selector)
          })
          .join(' / ')
          .toLowerCase()
        obj = {
          name: allNames,
          showCount: false,
          metricAttributesData: null
        }
      } else {
        obj = {
          name: this.getActualNodeName(id),
          totalProducts,
          showCount: true,
          metricAttributesData: itemData?.metricAttributesData
        }
      }
    }
    return obj
  })

  getHierarchy = computedFn(id => {
    return this.getHierarchyRecursively(
      id.split(CATEGORY_INTERNAL_SEPERATOR),
      this.dataStore.heirarchy
    )
  })

  getHierarchyByItemId = computedFn(id => {
    const [, index] = extractIndexFromId(id)
    const hierarchyId = findLastKey(
      this.hierarchyDetails,
      ({ startIndex, endIndex }) => index >= startIndex && index <= endIndex
    )
    return this.getHierarchy(hierarchyId)
  })

  getHierarchyByItemKey = computedFn(id => {
    const index = this.dataStore.itemIndexObjectStore.getIndexByKey(id)
    const hierarchyId = findLastKey(
      this.hierarchyDetails,
      ({ startIndex, endIndex }) => index >= startIndex && index <= endIndex
    )
    return this.getHierarchy(hierarchyId)
  })

  private getHierarchyRecursively = computedFn((id, hierarchy) => {
    const [first, ...rest] = id
    const firstHierarchy = find(hierarchy, ['name', first])
    return concat(
      firstHierarchy,
      rest?.length ? this.getHierarchyRecursively(rest, firstHierarchy.children) : []
    )
  })

  getItemByComputedId = computedFn((id: string) => {
    if (id?.includes(INDIVIDUAL_SEPARATOR_IDENTIFIER)) {
      const [, index] = extractIndexFromId(id)
      return this.dataStore.itemIndexObjectStore.indexObjectMap.get(index)
    }
    return this.hierarchyDetails[id]
  })

  getGroupObject = computedFn(
    (name, depth, children, startIndex, endIndex, totalItems) => ({
      name,
      isLeaf: false,
      depth: depth,
      children,
      startIndex,
      endIndex,
      totalItems
    })
  ) as any

  getChildObject = computedFn(
    (name, depth, children, startIndex, endIndex, totalItems) => ({
      name,
      isLeaf: false,
      depth: depth,
      children,
      startIndex,
      endIndex,
      totalItems
    })
  )

  protected getTotalItems = child => {
    return child?.totalProducts || 0
  }

  /**
   * This method will add cached implementation of computed functions to improve performance
   */
  protected initializeCaches() {
    this.caches.tableDataCache = memoize(
      (children, groupName, finished = 0, depth = 0, groupTotalItems = 0) => {
        return (children || []).reduce(
          (acc, child) => {
            let res = {}

            if (child?.children?.length) {
              const result = this.generateTableDataRecursively(
                child.children,
                `${groupName ? `${groupName}${CATEGORY_INTERNAL_SEPERATOR}` : ''}${
                  child.name
                }`,
                acc.finished,
                groupName ? depth + 1 : depth,
                this.getTotalItems(child)
              )
              const groupObj = groupName
                ? {
                    [groupName]: {
                      ...this.getGroupObject(
                        groupName,
                        depth,
                        ((acc[groupName] || {}).children || []).concat([
                          `${groupName}${CATEGORY_INTERNAL_SEPERATOR}${child.name}`
                        ]),
                        acc.finished,
                        maxBy(values(res), (val: any) =>
                          val.name.includes(groupName) ? val.endIndex : 0
                        ),
                        groupTotalItems
                      ),
                      attributeId: child.attributeId,
                      metricAttributesData: child.metricAttributesData,
                      variant: groupName ? 'h5' : 'h4'
                    }
                  }
                : {
                    [child.name]: {
                      ...this.getChildObject(
                        child.name,
                        depth + 1,
                        ((acc[groupName] || {}).children || []).map(
                          groupChild =>
                            `${child.name}${CATEGORY_INTERNAL_SEPERATOR}${groupChild.name}`
                        ),
                        acc.finished,
                        maxBy(values(res), (val: any) =>
                          val.name.includes(groupName) ? val.endIndex : 0
                        ),
                        undefined
                      ),
                      attributeId: child.attributeId,
                      metricAttributesData: child.metricAttributesData,
                      variant: groupName ? 'h5' : 'h4'
                    }
                  }
              if (this.isEnhancedViewEnabled) {
                // Adding attributeId param to hierarchy data to be used on TreeNav component
                res = {
                  ...groupObj,
                  ...Object.keys(result).reduce((acc, key) => {
                    const foundKey = groupObj[groupName]?.children?.find(
                      child => child === key
                    )
                    if (result[foundKey]?.attributeId) {
                      // Keep the other value from result object and update the attributeId
                      return {
                        ...acc,
                        [foundKey]: {
                          ...result[key],
                          attributeId: child?.attributeId,
                          metricAttributesData: child.metricAttributesData
                        }
                      }
                    }
                    if (groupObj[key]?.attributeId) {
                      // Keep the value from groupObj for the attributeId and merge the nested property
                      return {
                        ...acc,
                        [key]: {
                          ...result[key],
                          attributeId: groupObj[key]?.attributeId,
                          metricAttributesData: child.metricAttributesData,
                          variant: groupObj[key]?.variant
                        }
                      }
                    }
                    return { ...acc, [key]: result[key] }
                  }, {})
                }
              } else {
                res = {
                  ...groupObj,
                  ...Object.keys(result).reduce((acc, key) => {
                    if (groupObj[key]?.variant) {
                      return {
                        ...acc,
                        [key]: {
                          ...result[key],
                          variant: groupObj[key]?.variant
                        }
                      }
                    }
                    return { ...acc, [key]: result[key] }
                  }, {})
                }
              }
            } else {
              if (isNil(this.getTotalItems(child))) {
                return []
              }
              const children = fill(Array(this.getTotalItems(child)), null)
              const name = `${
                groupName ? `${groupName}${CATEGORY_INTERNAL_SEPERATOR}` : ''
              }${child.name}`
              const finished = acc.finished || 0
              const startIndx =
                (acc[groupName] || {}).startIndex === undefined ? finished : 0
              res = {
                [groupName]: {
                  ...this.getGroupObject(
                    groupName,
                    depth,
                    ((acc[groupName] || {}).children || []).concat([name]),
                    startIndx,
                    ((acc[groupName] || {}).endIndex || 0) +
                      finished +
                      Math.max(this.getTotalItems(child), 1) -
                      1,
                    groupTotalItems
                  ),
                  attributeId: child.attributeId,
                  metricAttributesData: child.metricAttributesData,
                  variant: 'h5'
                },
                [name]: {
                  ...this.getChildObject(
                    name,
                    depth + 1,
                    children,
                    finished,
                    finished + Math.max(this.getTotalItems(child), 1) - 1,
                    this.getTotalItems(child)
                  ),
                  attributeId: child.attributeId,
                  metricAttributesData: child.metricAttributesData,
                  isLeaf: true,
                  variant: groupName ? 'h5' : 'h4'
                },
                finished: finished + this.getTotalItems(child)
              }
            }
            return { ...acc, ...res }
          },
          { finished }
        )
      },
      (children, groupName, finished) => {
        const newChildren = map(
          children || [],
          child => `${child?.id}${child?.name}${this.getTotalItems(child)}`
        )

        return `${newChildren.join()} ${groupName} ${finished}`
      }
    )

    this.caches.sortedHierarchyDataCache = memoize(
      heirarchy => {
        return sortBy(Object.keys(heirarchy), [key => heirarchy[key].startIndex])
      },
      heirarchy => Object.keys(heirarchy).join()
    )
  }

  private initializeReactions() {
    this.reactions.push(
      reaction(
        () => this.dataStore.heirarchy,
        () => {
          if (this.dataStore.heirarchy?.length) {
            const heirarchy: any = omit(
              this.generateTableDataRecursively(this.dataStore.heirarchy, '', 0, 0, 0),
              ['finished']
            )
            const filteredHierarchy: any = {}
            Object.keys(heirarchy)
              .filter(key => key)
              .forEach(el => {
                filteredHierarchy[el] = heirarchy[el]
              })

            const processedHierarchy = {
              root: {
                children: map(this.dataStore.heirarchy, 'name')
              },
              ...mapValues(
                filteredHierarchy,
                ({
                  name,
                  children,
                  isLeaf,
                  depth,
                  totalItems,
                  attributeId,
                  metricAttributesData,
                  variant
                }) => ({
                  name: name.split(CATEGORY_INTERNAL_SEPERATOR).pop(),
                  depth,
                  totalItems,
                  attributeId,
                  metricAttributesData,
                  variant,
                  ...(isLeaf ? {} : { children })
                })
              )
            }
            this.scrollPositionStore.setHierarchy(processedHierarchy)
          } else {
            this.scrollPositionStore.setHierarchy({
              root: {
                children: []
              }
            })
          }
        }
      )
    )
    this.reactions.push(
      reaction(
        () => {
          return this.dataStore.heirarchyLoading
        },
        () => {
          if (!this.dataStore.heirarchyLoading) {
            this.reactions.push(
              when(
                () => !!this.dataStore.itemIndexObjectStore.getKeyByIndex(0),
                () => {
                  this.setActiveItemIndexAndScroll(0)
                },
                { timeout: 5 * 1000 }
              )
            )
          }
        }
      )
    )
    this.reactions.push(
      reaction(
        () => {
          return this.dataStore.filterStore.appliedFilters
        },
        () => {
          this.activeItemStore.setIndex(-1)
        }
      )
    )
    this.reactions.push(
      reaction(
        () => {
          return this.dataStore.searchKey
        },
        () => {
          this.activeItemStore.setIndex(-1)
        }
      )
    )
    this.addReactionToUpdateNavigation()
  }

  @action
  setActiveItemIndexAndScroll = (index: number) => {
    this.activeItemStore.setIndex(index)
    const id = this.dataStore.itemIndexObjectStore.indexObjectMap.get(
      this.activeItemStore.index
    )?.id
    if (!id) {
      return
    }
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(id)
    // set scroll position
    if (product) {
      const original = product?.original
      const hierarchyObj = {
        family: original?.family?.name,
        line: original?.line?.name
      }
      let heirarchyStr = ''
      if (this.isEnhancedViewEnabled) {
        heirarchyStr = product.hierarchyItems?.join(CATEGORY_INTERNAL_SEPERATOR)
      } else {
        heirarchyStr = [original?.activity?.name]
          .concat(
            (
              ACTIVITY_HIERARCHY_MAP?.[original?.activity?.name] ||
              DEFAULT_ACTIVITY_HIERARCHY
            ).map(val => hierarchyObj[val])
          )
          .filter(val => val)
          .join(CATEGORY_INTERNAL_SEPERATOR)
      }

      this.scrollPositionStore.scrollToNode(
        `${heirarchyStr}${INDIVIDUAL_SEPARATOR_IDENTIFIER}${this.activeItemStore.index}`
      )
    }
  }

  private addReactionToUpdateNavigation() {
    this.reactions.push(
      when(
        () =>
          this.dataStore.heirarchyLoading
            ? false
            : !!this.dataStore.heirarchy?.length &&
              !!Object.keys(this.scrollPositionStore.hierarchy || {}).length,
        () => {
          this.scrollPositionStore.scrollToNode(
            this.getFirstFromHierarchy(this.dataStore.heirarchy).join(
              CATEGORY_INTERNAL_SEPERATOR
            )
          )
        }
      )
    )
  }

  private getFirstFromHierarchy = heirarchy => {
    return heirarchy?.[0]
      ? [heirarchy?.[0]?.name].concat(this.getFirstFromHierarchy(heirarchy[0].children))
      : []
  }

  @action
  updateViewDimensions = dimensions => {
    this.dimensionMapper.setDefault(dimensions)
    this.elementRefStore.ref?.recomputeTree()
    // reset scrolled node to one present in the store
    // to match sidebar selection
    // as render doesn't change scroll top
    this.scrollPositionStore.scrollToNode(
      this.currentHierarchy.join(CATEGORY_INTERNAL_SEPERATOR)
    )
  }

  @action
  setCurrentHierarchy = (currentHierarchy: string) => {
    this.scrollPositionStore.scrollToNode(currentHierarchy)
  }

  @action
  toggleSidebar = () => {
    this.isSidebarOpen = !this.isSidebarOpen
  }

  @action
  toggleCardSizeModal = (...args) => {
    if (args?.length) {
      this.isCardSizeModalOpen = args[0]
    } else {
      this.isCardSizeModalOpen = !this.isCardSizeModalOpen
    }
  }

  @action
  clearDataCaches = () => {
    this.caches?.sortedHierarchyDataCache?.cache?.clear()
    this.caches?.tableDataCache?.cache?.clear()
  }

  @action
  onSearchKeyChange = (key: string) => {
    this.isSearchTouched = true
    // TODO: Need to implement (Network) request cancellation mechanism
    this.clearDataCaches()
    this.dataStore.applySearch(key)
    this.saveAppliedEnhancedViewActions('search', {
      key: this.dataStore.searchKey
    })
    this.addReactionToUpdateNavigation()
  }

  @action
  toggleFilterModal = (...args) => {
    if (args?.length) {
      this.isFilterActive = args[0]
    } else {
      this.isFilterActive = !this.isFilterActive
    }
  }

  @action
  toggleSortModal = (...args) => {
    if (args?.length) {
      this.isSortActive = args[0]
    } else {
      this.isSortActive = !this.isSortActive
    }
  }

  @action
  toggleIsSearchActive = () => {
    this.isSearchActive = !this.isSearchActive
    if (!this.isSearchActive) {
      this.onSearchKeyChange('')
    }
  }

  @action
  hideShowSearch = val => {
    this.isSearchActive = val
  }

  @action
  setAppliedFilters = filters => {
    this.isFilterTouched = true
    this.clearDataCaches()
    this.dataStore.applyFilters(
      Object.keys(filters).reduce(
        (acc, key) => ({ ...acc, [key]: filters[key].selected }),
        {}
      )
    )
    this.toggleFilterModal()
    this.addReactionToUpdateNavigation()
  }

  @action
  saveAppliedEnhancedViewActions = (action, data) => {
    const {
      nav: {
        location: { pathname },
        queryParams: { view }
      },
      productConfiguration: { saveAppliedGroupFilterSortSearch }
    } = stores
    const isOrderPage =
      pathname.endsWith(`/${ENHANCED_VIEW_ROUTES.orderCollection}`) &&
      (!view || view === View.Product || view === View.List)
    if (isOrderPage && action) {
      saveAppliedGroupFilterSortSearch(action, data, 'orderCollection')
    }
  }

  @action setNewAppliedFilters = () => {
    this.saveAppliedEnhancedViewActions('filter', {
      selected: this.dataStore.cnfProductFilterStore.selected,
      excludedFilters: this.dataStore.cnfProductFilterStore.excludedFilters
    })
    this.isFilterTouched = true
    this.clearDataCaches()
    this.dataStore.applyNewFilters()
    this.addReactionToUpdateNavigation()
  }

  @action
  applyGroupByOptions = (flag, options) => {
    this.isGroupByTouched = true
    const isModified = this.dataStore.cnfGroupByUIStore.applyGroupByOptions(flag, options)
    this.saveAppliedEnhancedViewActions(
      'group',
      this.dataStore.cnfGroupByUIStore.addedGroupByAttributes
    )
    if (isModified) {
      this.clearDataCaches()
      // Using new filters method to update group/hierarchy data
      this.scrollPositionStore.updateScrollToNode(null)
      this.dataStore.applyNewFilters()
      this.addReactionToUpdateNavigation()
    }
  }

  @action
  applyEnhancedSortByOptions = (id: string, orderBy?: string) => {
    this.isEnhancedSortByTouched = true
    this.dataStore.enhancedSortStore.handleSortChange(id, orderBy)
    this.saveAppliedEnhancedViewActions(
      'sort',
      this.dataStore.enhancedSortStore.selectedSortOption
    )
    this.clearDataCaches()
    // Using new filters method to update group/hierarchy data
    this.dataStore.applyNewFilters()
    this.addReactionToUpdateNavigation()
  }

  @action
  toggleGroupByModal = (...args) => {
    if (args?.length) {
      this.isGroupByActive = args[0]
    } else {
      this.isGroupByActive = !this.isGroupByActive
    }
  }

  @action
  toggleEnhancedSortModal = (...args) => {
    this.dataStore.enhancedSortStore.updateSortSearchText('')
    if (args?.length) {
      this.isEnhancedSortActive = args[0]
    } else {
      this.isEnhancedSortActive = !this.isEnhancedSortActive
    }
  }

  @action
  setAppliedSortOptions = sortOptions => {
    this.isSortTouched = true
    this.clearDataCaches()
    this.dataStore.applySorting(sortOptions)
    this.toggleSortModal()
    this.addReactionToUpdateNavigation()
  }

  @action
  applySortOptionsAgain = () => {
    this.isSortTouched = true
    this.clearDataCaches()
    this.dataStore.applySortingAgain()
    this.addReactionToUpdateNavigation()
    this.dataStore.setQuantityUpdated(false)
  }

  @action
  applyEnhancedWarningFilters = () => {
    this.isFilterTouched = true
    this.clearDataCaches()
    this.dataStore.applyNewFilters()
    this.addReactionToUpdateNavigation()
    this.dataStore.setQuantityUpdated(false)
  }

  @action resetSortData = () => {
    this.dataStore.setQuantityUpdated(false)
    this.dataStore.sortStore.resetSortData()
  }

  @action
  removeFilterValue = (field, value) => {
    this.isFilterTouched = true
    this.dataStore.applyFilters({
      ...this.dataStore.filterStore.appliedFilters,
      [field]: difference(this.dataStore.filterStore.appliedFilters[field], [value])
    })
    this.addReactionToUpdateNavigation()
  }

  @action
  removeFilterField = field => {
    this.isFilterTouched = true
    this.dataStore.applyFilters(omit(this.dataStore.filterStore.appliedFilters, [field]))
    this.addReactionToUpdateNavigation()
  }

  @action
  clearAllFilters = () => {
    this.isFilterTouched = true
    this.dataStore.filterStore.clearAppliedFilters()
    this.addReactionToUpdateNavigation()
    this.dataStore.applyFilters(this.dataStore.filterStore.appliedFilters)
  }

  /**
   * This method is an abstract method over data store which should resolve data which will needed to show a particular heirarchy
   * The logi implemented curretly fetches data on basis of family line and activity, however we need to have chunk processing
   * logic which will be implemented once we are using pagination
   * @param id
   * @param fromIndex
   * @param toIndex
   * @returns
   */
  @action
  fetchData(id: string, fromIndex?: number, toIndex?: number) {
    // let start, end, hierarchy
    // if (id.includes('-')) {
    //   hierarchy = id.split('-')[0]
    //   const { start: startIndex, end: endIndex } =
    //     this.dataStore.getIndexesFromHeirachy(hierarchy)
    //   start = startIndex
    //   end = endIndex
    // } else if (id.includes('@')) {
    //   const [hierarchyName, index] = id.split('@')
    //   hierarchy = hierarchyName
    //   start = end = +index
    // }
    if (id === this.hierarchyRootId) {
      return
    }
    const hierarchy = id.includes(PAGE_SEPARATOR_IDENTIFIER)
      ? id.split(PAGE_SEPARATOR_IDENTIFIER)[0]
      : id.split(INDIVIDUAL_SEPARATOR_IDENTIFIER)[0]
    const { start, end } = this.dataStore.getIndexesFromHierarchy(
      hierarchy.split(CATEGORY_INTERNAL_SEPERATOR),
      this.isEnhancedViewEnabled
    )
    let isParentNode = false
    if (this.isEnhancedViewEnabled) {
      isParentNode = !!this.scrollPositionStore.hierarchy?.[id]?.children?.length
    }
    if (
      !this.dataStore.heirarchyLoading &&
      !isNil(start) &&
      !isNil(end) &&
      !this.dataStore.hasItemsInRange(start, end) &&
      !this.dataStore.isProductsDataLoading(start, end) &&
      !this.isProductRemoving &&
      !isParentNode
    ) {
      this.dataStore.fetchItems(start, end)
    }
    return Promise.resolve([])
  }

  @action
  removeItemObjectFromHierarchy = async (
    index: number,
    groupId: string,
    isProductRemoved?: Boolean
  ) => {
    const itemId =
      this.dataStore.itemIndexObjectStore.indexObjectMap.get(index)?.[
        this.dataStore.itemIndexObjectStore.keyField
      ]
    try {
      await this.dataStore.removeItemObjectFromHierarchy(
        index,
        this.removedProductsResCache[itemId],
        isProductRemoved
      )
      // Check id on same index again as search may be applied if you get same search id then probably delete it otherwise
      // ignore since data would might be changed due to search/filter or any other operation
      // because we are deleting based on index
      const productIndex = this.dataStore.itemIndexObjectStore.getIndexByKey(itemId)
      isProductRemoved && this.setActiveItemIndexAndScroll(this.activeItemStore.index - 1)
      transaction(() => {
        // Remove Hierarchy
        isProductRemoved &&
          this.dataStore.removeHierarchy(groupId.split(CATEGORY_INTERNAL_SEPERATOR))
        // Remove Product
        isProductRemoved &&
          this.dataStore.itemIndexObjectStore.clearDataByRange(productIndex, productIndex)
      })
    } catch (e) {}
  }

  @computed get areObjectsDataLoading() {
    return this.dataStore.itemIndexObjectStore.objectLoadingProgress.every(map =>
      map.get('LOADING')
    )
  }

  @action onProductRemovedFromProductDetails = async (isProductRemoved?: any) => {
    const removedProductsCache = !isEmpty(isProductRemoved?.unBook)
      ? this.dataStore?.singleDoorResCache
      : reduce(
          Object.keys(isProductRemoved.removeBtn),
          (acc, bspId) => {
            acc[bspId] = this.removedProductsResCache[bspId]
            return acc
          },
          {}
        )
    this.isProductRemoving = true
    try {
      await Object.keys(removedProductsCache).forEach(bspId => {
        const productIndex = this.dataStore.itemIndexObjectStore.getIndexByKey(bspId)
        const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(bspId)

        const groupId = this.getFocusedProductHierarchy(
          productIndex === -1 ? 0 : productIndex
        )
        const groupIds = this.isEnhancedViewEnabled
          ? product?.hierarchyItems || []
          : groupId.split(CATEGORY_INTERNAL_SEPERATOR)
        this.dataStore.removeItemObjectFromHierarchy(
          productIndex,
          removedProductsCache[bspId],
          true
        )
        this.setActiveItemIndexAndScroll(this.activeItemStore.index - 1)
        transaction(async () => {
          // Remove Hierarchy
          // !bspId &&
          this.dataStore.removeHierarchy(groupIds)
          // Remove Product
          this.dataStore.itemIndexObjectStore.clearDataByRange(productIndex, productIndex)
        })
      })
      this.removedProductsResCache = {}
      this.dataStore.singleDoorResCache = {}
    } catch (e) {
    } finally {
      this.isProductRemoving = false
    }
  }

  /**
   * Helper method to get heirarchy string based on index of product
   *
   * @param index
   * @returns
   */
  getFocusedProductHierarchy = (index: number) => {
    return findKey(
      omit(this.hierarchyDetails, this.hierarchyRootId),
      hierarchyObj => hierarchyObj.startIndex <= index && hierarchyObj.endIndex >= index
    )
  }

  getEnhancedFocusedProductHierarchy = (index: number) => {
    return findKey(
      omit(this.hierarchyDetails, this.hierarchyRootId),
      hierarchyObj =>
        hierarchyObj.isLeaf &&
        hierarchyObj.startIndex <= index &&
        hierarchyObj.endIndex >= index
    )
  }

  /**
   * This method should be called when are destroying UI dependencies
   */
  @action
  destroy() {
    Object.keys(this.caches).forEach(cacheKey => {
      this.caches[cacheKey].cache.clear()
    })
    this.reactions.forEach(disposer => disposer())
    this.reactions = []
    this.isFilterActive =
      this.isSearchActive =
      this.isFilterTouched =
      this.isSearchTouched =
      this.isSidebarOpen =
      this.isSortTouched =
      this.isGroupByTouched =
      this.isEnhancedSortByTouched =
        false
    this.dataStore.destroy()
    this.scrollPositionStore.reset()
    this.dimensionMapper.clearAll()
    this.activeItemStore.setIndex(-1)
  }

  @computed get isFilterOrSearchApplied() {
    return this.dataStore.filterStore.hasFilters || !!this.dataStore.searchKey
  }

  // *** Product Details ***
  private removedProductsResCache: any = {}

  @action onProductDetailClick = (bspId: string) => (e?) => {
    const {
      nav: { updateQueryParams },
      productConfiguration: { initialAllocationActivated }
    } = stores
    if (!initialAllocationActivated) {
      if (!e?.shiftKey) {
        updateQueryParams({ bspId }, 'push')
      }
    }
  }

  updateRemovedProductResponseCache = async (bspId: string, res: any) => {
    this.removedProductsResCache[bspId] = await res
  }

  @action
  onNextButtonClick = () => {
    const nextFocusedProductIndex = this.activeItemStore.index + 1
    const nextBSPId = this.dataStore.itemIndexObjectStore.indexObjectMap.get(
      nextFocusedProductIndex
    )?.id
    if (nextBSPId) {
      this.onProductDetailClick(nextBSPId)()
      this.setActiveItemIndexAndScroll(nextFocusedProductIndex)
    }
  }

  @action
  onPreviousButtonClick = () => {
    const nextFocusedProductIndex = this.activeItemStore.index - 1
    const nextBSPId = this.dataStore.itemIndexObjectStore.indexObjectMap.get(
      nextFocusedProductIndex
    )?.id
    if (nextBSPId) {
      this.onProductDetailClick(nextBSPId)()
      this.setActiveItemIndexAndScroll(nextFocusedProductIndex)
    }
  }

  @action
  setActiveFocusProductIndex = () => {
    const {
      queryParams: { bspId }
    } = nav
    const focusedProductIndex = this.dataStore.itemIndexObjectStore.getIndexByKey(bspId)

    if (focusedProductIndex > -1) {
      this.setActiveItemIndexAndScroll(focusedProductIndex)
    }
  }

  @computed get isNextProductAvailable() {
    return this.dataStore.totalProducts - 1 <= this.activeItemStore.index
  }

  @computed get isPreviousProductAvailable() {
    return this.activeItemStore.index <= 0
  }

  @action restoreOrdersTopbarSelections = () => {
    const {
      productConfiguration: { appliedGroupFilterSort }
    } = stores
    this.dataStore.cnfProductFilterStore.selected =
      appliedGroupFilterSort?.orderCollection?.filter?.selected || {}
    this.dataStore.cnfProductFilterStore.excludedFilters =
      appliedGroupFilterSort?.orderCollection?.filter?.excludedFilters || {}
    this.dataStore.enhancedSortStore.selectedSortOption =
      appliedGroupFilterSort?.orderCollection?.sort || {}
    this.dataStore.cnfGroupByUIStore.addedGroupByAttributes =
      appliedGroupFilterSort?.orderCollection?.group || []
    this.dataStore.searchKey = appliedGroupFilterSort?.orderCollection?.search?.key || ''
  }
}
